import type { ColourTarget, PaletteCell } from "../../../types";
import styles from "./ColourToolsPanel.module.css";

interface PaletteSectionProps {
  readonly target: ColourTarget;
  readonly onTargetChange: (t: ColourTarget) => void;
  readonly palette: PaletteCell[][];
  readonly themeLoading: boolean;
  readonly busy: boolean;
  readonly selectedHex: string | null;
  readonly customHex: string;
  /** Called with a `#rrggbb` value when the custom colour is picked. */
  readonly onCustomPick: (hexWithHash: string) => void;
  readonly onSwatchClick: (hex: string) => void;
}

const TARGETS: readonly ColourTarget[] = ["fill", "line", "font"];

/** Theme palette grid + custom colour picker. Clicking a swatch (or picking a
 * custom colour) applies it immediately to the active Fill/Line/Font target —
 * no separate Apply step. Owns no state of its own beyond what's passed in. */
export function PaletteSection({
  target,
  onTargetChange,
  palette,
  themeLoading,
  busy,
  selectedHex,
  customHex,
  onCustomPick,
  onSwatchClick,
}: Readonly<PaletteSectionProps>) {
  return (
    <div className={styles.paletteSection}>
      <span className={styles.paletteHeading}>Theme palette — click a colour to apply</span>

      {/* Target segment (which property the chosen colour is applied to) */}
      <div className={styles.targetTabs} aria-label="Apply colour to">
        {TARGETS.map((t) => (
          <button
            type="button"
            key={t}
            aria-pressed={target === t}
            className={`${styles.targetTab} ${target === t ? styles.targetTabActive : ""}`}
            onClick={() => onTargetChange(t)}
            data-testid={`target-tab-${t}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Palette grid */}
      {palette.length > 0 ? (
        <div
          className={styles.paletteWrapper}
          role="radiogroup"
          aria-label="Theme colour palette"
        >
          {palette.map((row) => (
            // Keyed by the row's own `row` field (assigned once, per luminosity
            // step, when the grid is built) rather than the .map() position —
            // every cell in a row shares this value, so it's a stable row
            // identity independent of where the row happens to render.
            <div key={`palette-row-${row[0]!.row}`} className={styles.paletteRow}>
              {row.map((cell) => {
                // key/row together uniquely identify a cell in the grid —
                // key alone repeats across rows, row alone repeats across
                // columns, but the pair never collides.
                const cellKey = `${cell.key}-${cell.row}`;
                const isActive = selectedHex === cell.hex;
                return (
                  <button
                    type="button"
                    key={cellKey}
                    className={`${styles.paletteSwatch} ${isActive ? styles.paletteSwatchActive : ""}`}
                    style={{ background: "#" + cell.hex }}
                    onClick={() => onSwatchClick(cell.hex)}
                    title={`${cell.label} — #${cell.hex}`}
                    aria-label={`${cell.label} — #${cell.hex}`}
                    role="radio"
                    aria-checked={isActive}
                    disabled={busy}
                    data-testid={`palette-swatch-${cellKey}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          {themeLoading ? "Loading theme…" : "Load theme to see palette."}
        </div>
      )}

      {/* Custom colour — pick any colour; applied immediately to the active target */}
      <div className={styles.customRow}>
        <span className={styles.customLabel}>Custom colour</span>
        <label className={styles.customSwatch} title="Pick any colour">
          <input
            type="color"
            value={customHex}
            onChange={(e) => onCustomPick(e.target.value)}
            aria-label="Custom colour"
            disabled={busy}
            data-testid="custom-hex-input"
          />
          <span className={styles.customPlus} aria-hidden="true">
            +
          </span>
        </label>
        <span className={styles.customHint}>
          Pick any colour to apply as <strong>{target}</strong>
        </span>
      </div>

      {palette.length > 0 && (
        <p className={styles.paletteFoot}>
          Palette is extracted from the <strong>active presentation&apos;s</strong> theme — 10 base
          colours with 5 tints &amp; shades each (60 swatches).
        </p>
      )}
    </div>
  );
}
